import React from 'react';
import { Typography, Button, Tooltip, Row, Col } from 'antd';
import { ReloadOutlined, EyeInvisibleOutlined, EyeOutlined } from '@ant-design/icons';
import { useResponsive } from '../hooks/useResponsive';
import { useAppSelector, useAppDispatch } from '../hooks/redux';
import { baseApi } from '../services/baseApi';
import { useGetDashboardPollingQuery } from '../services/analyticsApi';
import { useRestaurantTimeZone } from '../hooks/useTimeZone';
import DashboardMetrics from '../components/dashboard/DashboardMetrics';
import OperationalStatus from '../components/dashboard/OperationalStatus';
import RealTimeCharts from '../components/dashboard/RealTimeCharts';
import PaymentMethodsCard from '../components/dashboard/PaymentMethodsCard';
import RejectedShiftClosingAlert from '../components/cashRegister/RejectedShiftClosingAlert';
import DashboardStatusStrip from '../components/dashboard/DashboardStatusStrip';
import OnboardingChecklist from '../components/dashboard/OnboardingChecklist';
import { DASHBOARD_QUERY } from '../components/dashboard/dashboardQuery';
import { useMoneyPrivacy } from '../hooks/useMoneyPrivacy';

const { Title, Text } = Typography;

const Dashboard: React.FC = () => {
  const { isMobile } = useResponsive();
  const dispatch = useAppDispatch();
  const { currentRestaurant } = useAppSelector((state) => state.restaurant);
  const { formatInTimeZone } = useRestaurantTimeZone();
  const { allHidden, anyHidden, toggleAll } = useMoneyPrivacy();

  // Se suscribe a la misma query que los widgets (mismo cache key, cero
  // requests extra) solo para mostrar la hora de la última actualización.
  const { data: dashboard, isFetching } = useGetDashboardPollingQuery(
    {
      restaurantId: currentRestaurant?.id || '',
      query: DASHBOARD_QUERY,
    },
    { skip: !currentRestaurant?.id }
  );

  const handleRefresh = () => {
    dispatch(baseApi.util.invalidateTags(['Analytics']));
  };

  return (
    <div>
      {/* Header */}
      <div style={{
        marginBottom: '24px',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: isMobile ? 'flex-start' : 'center',
        flexDirection: isMobile ? 'column' : 'row',
        gap: isMobile ? '12px' : '0'
      }}>
        <div>
          <Title level={2} style={{ margin: 0 }}>
            Dashboard
          </Title>
          <Text type="secondary">
            Métricas en tiempo real del restaurante
          </Text>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          {dashboard?.generatedAt && (
            <Text type="secondary" style={{ fontSize: isMobile ? '11px' : '12px' }}>
              Actualizado {formatInTimeZone(dashboard.generatedAt, 'HH:mm')}
            </Text>
          )}
          {/* Interruptor general: un toque tapa TODOS los importes de la página.
              Es el gesto que importa —se acerca un empleado y no da tiempo de
              buscar los ojitos de cada tarjeta—, por eso va junto al de
              actualizar y con el mismo tamaño de toque. */}
          <Tooltip title={allHidden ? 'Mostrar los importes' : 'Ocultar los importes'}>
            <Button
              icon={allHidden ? <EyeInvisibleOutlined /> : <EyeOutlined />}
              onClick={toggleAll}
              aria-label={allHidden ? 'Mostrar los importes' : 'Ocultar los importes'}
              aria-pressed={allHidden}
              type={anyHidden ? 'primary' : 'default'}
              size={isMobile ? 'middle' : 'large'}
              style={{
                minWidth: isMobile ? 40 : 44,
                minHeight: isMobile ? 40 : 44,
              }}
            />
          </Tooltip>
          <Tooltip title="Actualizar datos">
            <Button
              icon={<ReloadOutlined />}
              onClick={handleRefresh}
              loading={isFetching}
              size={isMobile ? 'middle' : 'large'}
              style={{
                minWidth: isMobile ? 40 : 44,
                minHeight: isMobile ? 40 : 44,
              }}
            />
          </Tooltip>
        </div>
      </div>

      {/* Debug Info - Temporary */}
      {!currentRestaurant && (
        <div style={{ padding: '20px', background: 'var(--bg-error-light)', border: '1px solid var(--border-error)', borderRadius: '8px', marginBottom: '24px' }}>
          <Title level={4} style={{ color: 'var(--color-error-text)', marginTop: 0 }}>
            ⚠️ No hay restaurante seleccionado
          </Title>
          <Text>
            Por favor selecciona un restaurante en el menú superior o en la configuración para ver el dashboard.
          </Text>
        </div>
      )}

      {/* Primeros pasos: solo mientras no haya menú y una venta cobrada. Va antes que
          todo porque es lo único que importa el primer día. */}
      <OnboardingChecklist />

      {/* Estado del restaurante, resumen IA, menú del día y novedades */}
      <DashboardStatusStrip />

      {/* Alertas de cierres rechazados — accionable y urgente, va aparte de la tira */}
      <RejectedShiftClosingAlert />

      {/* Métricas principales */}
      <DashboardMetrics />

      {/* Ventas por hora + métodos de pago */}
      <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
        <Col xs={24} lg={16}>
          <RealTimeCharts />
        </Col>
        <Col xs={24} lg={8}>
          <PaymentMethodsCard />
        </Col>
      </Row>

      {/* Estado operacional */}
      <OperationalStatus />
    </div>
  );
};

export default Dashboard;
